import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { AxiosResponse, AxiosError } from 'axios';

// PKCE Helper functions
const generateRandomString = (length: number): string => {
  const array = new Uint32Array(length / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

const base64urlencode = (a: ArrayBuffer): string => {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(a) as any))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const hashed = await sha256(verifier);
  return base64urlencode(hashed);
};


// Placeholder icons - replace with actual icons if you have them
import { ExternalLink, AlertTriangle, CheckCircle, Monitor, Smartphone, Tablet, LogOut } from 'lucide-react'; // Assuming these might be useful, or replace with actual brand icons

// Placeholder icons - replace with actual icons if you have them
// For now, we'll keep the simple text placeholders but ideally use SVGs or a library like lucide-react for brand icons if available
const GoogleIcon = () => <img src="/assets/googleicon.svg" alt="Google" className="w-5 h-5 mr-2" />;
const TwitchIcon = () => <img src="/assets/twitchicon.svg" alt="Twitch" className="w-5 h-5 mr-2" />;
const KickIcon = () => <img src="/assets/kickicon.svg" alt="Kick" className="w-5 h-5 mr-2" />; // Add Kick icon

// Describes the user object from the API (e.g., /api/auth/me)
interface ApiUser {
  userId: string;
  email: string;
  username: string;
  role: string; // Raw from backend, needs casting for FrontendUser
  name?: string;
  googleId?: string;
  twitchId?: string;
  kickId?: string; // Add kickId
  profileImageUrl?: string;
  firstLoginProvider?: 'GOOGLE' | 'TWITCH' | 'KICK' | 'EMAIL' | string; // Allow string for flexibility
}

// Describes the response structure for /api/auth/me
interface AuthMeApiResponseData {
  success: boolean;
  user: ApiUser; // Uses ApiUser for the raw response
  message?: string;
}

// Describes the user structure for the frontend context (should match AuthContext's User)
interface FrontendUser {
  id: number;
  email: string;
  username: string;
  role: 'USER' | 'ADMIN';
  name?: string;
  googleId?: string | null;
  twitchId?: string | null;
  kickId?: string | null; // Add kickId
  profileImageUrl?: string | null;
  firstLoginProvider?: string | null;
}

interface OAuthURLResponseData {
  success: boolean;
  url?: string;
  message?: string;
}

interface GeneralApiResponse {
  success: boolean;
  message?: string;
}

// For username update, assume it returns a partial ApiUser structure
interface UpdateUsernameResponseData {
  success: boolean;
  user: Partial<ApiUser>;
  message?: string;
}

interface DeviceSession {
  id: number;
  deviceId: string;
  deviceName?: string;
  deviceType?: string;
  userAgent?: string;
  ipAddress?: string;
  lastActivity: string;
  createdAt: string;
  isCurrent: boolean;
}


const AccountSettingsPage: React.FC = () => {
  const { user, isLoading, setUser } = useAuth(); // Assuming setUser exists
  const location = useLocation();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  const [newUsername, setNewUsername] = useState(user?.username || '');
  const [usernameMessage, setUsernameMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isUsernameSubmitting, setIsUsernameSubmitting] = useState(false);

  const [notification, setNotification] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isConnectingOAuth, setIsConnectingOAuth] = useState<null | 'google' | 'twitch' | 'kick'>(null);
  
  const [deviceSessions, setDeviceSessions] = useState<DeviceSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.username && !newUsername) {
      setNewUsername(user.username);
    }
  }, [user?.username]);

  // Fetch fresh user data when the component mounts to ensure UI reflects the latest state
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response: AxiosResponse<AuthMeApiResponseData> = await api.get('/auth/me');
        if (response.data.success && response.data.user && setUser) {
          const apiUser = response.data.user;
          const frontendUser: FrontendUser = {
            id: parseInt(apiUser.userId, 10),
            email: apiUser.email,
            username: apiUser.username,
            role: apiUser.role as 'USER' | 'ADMIN',
            name: apiUser.name,
            googleId: apiUser.googleId,
            twitchId: apiUser.twitchId,
            kickId: apiUser.kickId, // Add kickId
            profileImageUrl: apiUser.profileImageUrl,
            firstLoginProvider: apiUser.firstLoginProvider,
          };
          setUser(frontendUser);
          // Update local state for username if it's different and not yet edited by user
          if (frontendUser.username && newUsername !== frontendUser.username && newUsername === (user?.username || '')) {
            setNewUsername(frontendUser.username); // Corrected from freshUser to frontendUser
          }
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error('Failed to fetch current user in AccountSettingsPage:', axiosError.response?.data || axiosError.message);
        // Handle error appropriately, e.g., show a notification or rely on AuthContext's error handling
      }
    };

    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser]); // Run only on mount and when setUser changes (which it shouldn't)

  // Fetch device sessions when component mounts
  useEffect(() => {
    if (user) {
      fetchDeviceSessions();
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkSuccess = params.get('link_success');
    const linkError = params.get('link_error');
    const message = params.get('message');
    const errorMessage = params.get('error_message');

    if (linkSuccess) {
      setNotification({ type: 'success', text: message || `${linkSuccess.charAt(0).toUpperCase() + linkSuccess.slice(1)} account linked successfully!` });
      // Refresh user data to reflect the new link
      api.get('/auth/me').then((response: AxiosResponse<AuthMeApiResponseData>) => {
        if (response.data.success && response.data.user && setUser) {
          const apiUser = response.data.user;
          const frontendUser: FrontendUser = {
            id: parseInt(apiUser.userId, 10),
            email: apiUser.email,
            username: apiUser.username,
            role: apiUser.role as 'USER' | 'ADMIN',
            name: apiUser.name,
            googleId: apiUser.googleId,
            twitchId: apiUser.twitchId,
            kickId: apiUser.kickId, // Add kickId
            profileImageUrl: apiUser.profileImageUrl,
            firstLoginProvider: apiUser.firstLoginProvider,
          };
          setUser(frontendUser);
        }
      }).catch((err: AxiosError | any) => console.error("Failed to refresh user after linking:", err.response?.data || err.message));
      navigate(location.pathname, { replace: true }); // Clear URL params
    }
    if (linkError) {
      setNotification({ type: 'error', text: errorMessage || `Failed to link ${linkError} account. Please try again.` });
      navigate(location.pathname, { replace: true }); // Clear URL params
    }
  }, [location, navigate, setUser]);

  const handleConnect = async (provider: 'google' | 'twitch' | 'kick') => {
    if (isConnectingOAuth) return; // Prevent multiple clicks
    setIsConnectingOAuth(provider);
    setNotification(null); // Clear previous notifications
    try {
      if (provider === 'kick') {
        const codeVerifier = generateRandomString(64);
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        sessionStorage.setItem('kick_code_verifier', codeVerifier);

        // Since this is a linking action, the user is already authenticated.
        // The backend's redirectToKick will use the existing session.
        const response = await api.post('/auth/kick', {
          code_challenge: codeChallenge,
        });

        if (response.data.success && response.data.url) {
          window.location.href = response.data.url;
        } else {
           throw new Error(response.data.message || 'Failed to get Kick auth URL for linking.');
        }
      } else {
        // Existing logic for Google and Twitch
        const response: AxiosResponse<OAuthURLResponseData> = await api.get(`/auth/${provider}`);
        if (response.data.success && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error(response.data.message || `Could not initiate ${provider} connection.`);
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      console.error(`Error connecting with ${provider}:`, axiosError);
      setNotification({ type: 'error', text: `An error occurred while trying to connect with ${provider}. ${axiosError.response?.data?.message || (error as Error).message || 'Please try again.'}` });
      setIsConnectingOAuth(null);
    }
  };

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    if (newPassword.length < 6) { // Basic validation, backend has more robust
        setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
        return;
    }

    setIsPasswordSubmitting(true);
    try {
      const response: AxiosResponse<GeneralApiResponse> = await api.post('/auth/set-password', { newPassword });
      if (response.data.success) {
        setPasswordMessage({ type: 'success', text: response.data.message || 'Password updated successfully!' });
        setNewPassword('');
        setConfirmPassword('');
        // Optionally, you might want to inform the user that they might need to log in again
        // or that their session has been updated, though typically changing password
        // doesn't invalidate current session tokens unless specifically designed to do so.
      } else {
        setPasswordMessage({ type: 'error', text: response.data.message || 'Failed to update password.' });
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setPasswordMessage({ type: 'error', text: axiosError.response?.data?.message || 'An error occurred.' });
    } finally {
      setIsPasswordSubmitting(false);
    }
  };


  const handleUpdateUsername = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUsernameMessage(null);
    if (!newUsername.trim() || newUsername.trim().length < 3) {
      setUsernameMessage({ type: 'error', text: 'Username must be at least 3 characters long.' });
      return;
    }
    if (newUsername.trim() === user?.username) {
      setUsernameMessage({ type: 'info', text: 'This is already your current username.' } as any); // Use 'info' or a neutral type
      return;
    }

    setIsUsernameSubmitting(true);
    try {
      const response: AxiosResponse<UpdateUsernameResponseData> = await api.patch('/user/me/username', { newUsername: newUsername.trim() });
      console.log('[AccountSettingsPage] Username update response:', response.data); // DEBUG LOG
      if (response.data.success && response.data.user) {
        setUsernameMessage({ type: 'success', text: response.data.message || 'Username updated successfully!' });
        
        // Update user in AuthContext
        if (setUser) {
          setUser(prevUser => {
            if (!prevUser) return null; // Should not happen if user is on this page
            const partialApiUser = response.data.user; // This is Partial<ApiUser>
            
            // Create a new object for updates to avoid mutating prevUser directly before spreading
            const updatesForFrontendUser: Partial<FrontendUser> = {};

            if (partialApiUser.username !== undefined) {
              updatesForFrontendUser.username = partialApiUser.username;
            }
            if (partialApiUser.name !== undefined) { // name can be updated
              updatesForFrontendUser.name = partialApiUser.name;
            }
            if (partialApiUser.email !== undefined) { // email might be updatable
              updatesForFrontendUser.email = partialApiUser.email;
            }
             if (partialApiUser.profileImageUrl !== undefined) {
              updatesForFrontendUser.profileImageUrl = partialApiUser.profileImageUrl;
            }
            // Role, googleId, twitchId, firstLoginProvider are less likely to change on username update
            // but if backend can return them, we can handle:
            if (partialApiUser.role !== undefined) {
              updatesForFrontendUser.role = partialApiUser.role as 'USER' | 'ADMIN';
            }
            // ID should not change on username update. If backend sends userId, it's for identification, not update of ID.
            // If partialApiUser.userId is present and needs to update the ID (which is bad practice for a PATCH to username):
            // if (partialApiUser.userId !== undefined && prevUser.id !== parseInt(partialApiUser.userId, 10)) {
            //   updatesForFrontendUser.id = parseInt(partialApiUser.userId, 10);
            // }

            return { ...prevUser, ...updatesForFrontendUser };
          });
        } else {
          // Fallback or warning if setUser is not available
          console.warn("AuthContext's setUser function is not available. User state might be stale.");
        }

        // Save the new access token if it exists
        // if (response.data.newAccessToken) { // HttpOnly cookie kullanıldığı için localStorage'a yazmaya gerek yok
        //   localStorage.setItem('accessToken', response.data.newAccessToken);
        //   console.log('[AccountSettingsPage] New access token saved to localStorage.');
        // }

      } else {
        setUsernameMessage({ type: 'error', text: response.data.message || 'Failed to update username.' });
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setUsernameMessage({ type: 'error', text: axiosError.response?.data?.message || 'An error occurred.' });
    } finally {
      setIsUsernameSubmitting(false);
    }
  };

  const handleUnlinkTwitch = async () => {
    if (!user || !user.twitchId) {
      setNotification({ type: 'error', text: 'Twitch account is not linked.' });
      return;
    }
    const confirmUnlink = window.confirm('Are you sure you want to unlink your Twitch account? This action cannot be undone.');
    if (!confirmUnlink) {
      return;
    }

    try {
      const response: AxiosResponse<GeneralApiResponse> = await api.post(`/user/unlink-twitch`, {});

      if (response.data.success) {
        setNotification({
          type: 'success',
          text: "Twitch account unlinked. This Twitch ID cannot be linked to a different account or used to create a new account for 3 months. You can relink it to this account at any time."
        });
        if (setUser) {
           api.get('/auth/me').then((meResponse: AxiosResponse<AuthMeApiResponseData>) => {
            if (meResponse.data.success && meResponse.data.user) {
              const apiUser = meResponse.data.user;
              const frontendUser: FrontendUser = {
                id: parseInt(apiUser.userId, 10),
                email: apiUser.email,
                username: apiUser.username,
                role: apiUser.role as 'USER' | 'ADMIN',
                name: apiUser.name,
                googleId: apiUser.googleId,
                twitchId: apiUser.twitchId,
                kickId: apiUser.kickId,
                profileImageUrl: apiUser.profileImageUrl,
                firstLoginProvider: apiUser.firstLoginProvider,
              };
              setUser(frontendUser);
            }
          }).catch((err: AxiosError | any) => console.error("Failed to refresh user after unlinking Twitch:", err.response?.data || err.message));
        }
      } else {
        setNotification({ type: 'error', text: response.data.message || 'Failed to unlink Twitch account.' });
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setNotification({ type: 'error', text: axiosError.response?.data?.message || 'An error occurred while unlinking Twitch account.' });
    }
  };

  const handleUnlinkKick = async () => {
    if (!user || !user.kickId) {
      setNotification({ type: 'error', text: 'Kick account is not linked.' });
      return;
    }
    const confirmUnlink = window.confirm('Are you sure you want to unlink your Kick account?');
    if (!confirmUnlink) {
      return;
    }

    try {
      const response: AxiosResponse<GeneralApiResponse> = await api.post(`/user/unlink-kick`, {});

      if (response.data.success) {
        setNotification({ type: 'success', text: 'Kick account unlinked successfully.' });
        if (setUser) {
          api.get('/auth/me').then((meResponse: AxiosResponse<AuthMeApiResponseData>) => {
            if (meResponse.data.success && meResponse.data.user) {
              const apiUser = meResponse.data.user;
              const frontendUser: FrontendUser = {
                id: parseInt(apiUser.userId, 10),
                email: apiUser.email,
                username: apiUser.username,
                role: apiUser.role as 'USER' | 'ADMIN',
                name: apiUser.name,
                googleId: apiUser.googleId,
                twitchId: apiUser.twitchId,
                kickId: apiUser.kickId,
                profileImageUrl: apiUser.profileImageUrl,
                firstLoginProvider: apiUser.firstLoginProvider,
              };
              setUser(frontendUser);
            }
          }).catch((err: AxiosError | any) => console.error("Failed to refresh user after unlinking Kick:", err.response?.data || err.message));
        }
      } else {
        setNotification({ type: 'error', text: response.data.message || 'Failed to unlink Kick account.' });
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setNotification({ type: 'error', text: axiosError.response?.data?.message || 'An error occurred while unlinking Kick account.' });
    }
  };

  const fetchDeviceSessions = async () => {
    setIsLoadingSessions(true);
    setSessionsError(null);
    try {
      const response = await api.get('/auth/sessions');
      if (response.data.success) {
        setDeviceSessions(response.data.sessions);
      } else {
        setSessionsError(response.data.message || 'Failed to fetch device sessions');
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setSessionsError(axiosError.response?.data?.message || 'Failed to fetch device sessions');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleRevokeSession = async (deviceId: string) => {
    const confirmRevoke = window.confirm('Are you sure you want to sign out this device?');
    if (!confirmRevoke) return;

    try {
      const response = await api.delete(`/auth/sessions/${deviceId}`);
      if (response.data.success) {
        setNotification({ type: 'success', text: 'Device signed out successfully' });
        fetchDeviceSessions();
      } else {
        setNotification({ type: 'error', text: response.data.message || 'Failed to sign out device' });
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setNotification({ type: 'error', text: axiosError.response?.data?.message || 'Failed to sign out device' });
    }
  };

  const handleRevokeOtherSessions = async () => {
    const confirmRevoke = window.confirm('Are you sure you want to sign out all other devices? This will not affect your current session.');
    if (!confirmRevoke) return;

    try {
      const response = await api.post('/auth/sessions/revoke-others');
      if (response.data.success) {
        setNotification({ type: 'success', text: `Signed out ${response.data.revokedCount} other device(s)` });
        fetchDeviceSessions();
      } else {
        setNotification({ type: 'error', text: response.data.message || 'Failed to sign out other devices' });
      }
    } catch (error) {
      const axiosError = error as AxiosError<GeneralApiResponse>;
      setNotification({ type: 'error', text: axiosError.response?.data?.message || 'Failed to sign out other devices' });
    }
  };

  const getDeviceIcon = (deviceType?: string) => {
    if (!deviceType) return <Monitor className="w-5 h-5" />;
    const type = deviceType.toLowerCase();
    if (type.includes('mobile') || type.includes('phone')) return <Smartphone className="w-5 h-5" />;
    if (type.includes('tablet')) return <Tablet className="w-5 h-5" />;
    return <Monitor className="w-5 h-5" />;
  };

  const formatLastActivity = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (isLoading && !user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <p className="text-center text-red-500">Please log in to view account settings.</p>;
  }

  const isGoogleLinked = !!user.googleId;
  const isTwitchLinked = !!user.twitchId;
  const isKickLinked = !!user.kickId; // Add isKickLinked
  // A simple way to check if the user might have been created via OAuth without a password initially.
  // Backend would ideally provide a `hasPassword` flag.
  // For now, we assume if they have neither googleId nor twitchId, they likely have a password.
  // Or, if they have an OAuth ID, they might not have a password set yet.
  const mightNeedToSetPassword = isGoogleLinked || isTwitchLinked; // Simplified logic

  return (
    <div className="container mx-auto px-4 py-8 text-foreground">
      <h1 className="text-3xl font-bold mb-8">Account Settings</h1>

      {notification && (
        <div
          className={`p-4 mb-4 text-sm rounded-lg ${
            notification.type === 'success' ? 'bg-green-100 text-green-700 border border-green-400' : 'bg-red-100 text-red-700 border border-red-400'
          }`}
          role="alert"
        >
          <span className="font-medium">{notification.type === 'success' ? 'Success!' : 'Error!'}</span> {notification.text}
          <button onClick={() => setNotification(null)} className="float-right font-bold">X</button>
        </div>
      )}
      
      <div className="bg-card p-6 rounded-lg shadow-md border border-border mb-8">
        <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
        <form onSubmit={handleUpdateUsername} className="space-y-4 mb-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-muted-foreground">
              Username
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                id="username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
                className="flex-1 block w-full min-w-0 px-3 py-2 bg-background border border-input rounded-none rounded-l-md placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
              <button
                type="submit"
                disabled={isUsernameSubmitting || newUsername.trim() === user.username}
                className="inline-flex items-center px-4 py-2 border border-l-0 border-primary text-sm font-medium rounded-r-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
              >
                {isUsernameSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
            {usernameMessage && (
              <p className={`mt-2 text-sm ${usernameMessage.type === 'success' ? 'text-success' : usernameMessage.type === 'error' ? 'text-error' : 'text-info'}`}>
                {usernameMessage.text}
              </p>
            )}
          </div>
        </form>
        <p className="text-muted-foreground">Email: {user.email}</p>
        {user.name && <p className="text-muted-foreground">Name: {user.name}</p>}
      </div>

      <div className="bg-card p-6 rounded-lg shadow-md border border-border mb-8">
        <h2 className="text-xl font-semibold mb-6">Linked Accounts</h2>
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <GoogleIcon />
                <span className="font-medium">Google</span>
              </div>
              {isGoogleLinked ? (
                <div className="flex items-center">
                  <span className="text-sm text-success mr-3 flex items-center"><CheckCircle size={14} className="mr-1"/>Connected</span>
                  {user?.firstLoginProvider === 'GOOGLE' ? (
                     <span className="text-xs text-muted-foreground italic ml-2" title="This is your primary sign-in method and cannot be unlinked.">(Primary sign-in, cannot unlink)</span>
                  ) : (
                    <button
                      className="px-3 py-1 border border-border text-muted-foreground hover:bg-secondary/50 text-xs rounded-md disabled:opacity-50"
                      disabled // Unlink Google functionality not yet implemented in backend
                      title="Unlink Google functionality will be available soon."
                    >
                      Unlink (Soon)
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleConnect('google')}
                  disabled={!!isConnectingOAuth}
                  className="px-4 py-2 bg-[#4285F4] hover:bg-[#3578E5] text-white text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4285F4]/50 flex items-center disabled:opacity-50"
                >
                  {isConnectingOAuth === 'google' ? (
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <GoogleIcon />
                  )}
                  {isConnectingOAuth === 'google' ? 'Connecting...' : 'Connect with Google'}
                </button>
              )}
            </div>
            {isGoogleLinked && user.email && (
                 <p className="text-xs text-muted-foreground mt-1 pl-7">
                    Linked as {user.email}
                 </p>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <TwitchIcon />
                <span className="font-medium">Twitch</span>
              </div>
              {isTwitchLinked ? (
                <div className="flex items-center">
                  <span className="text-sm text-success mr-3 flex items-center"><CheckCircle size={14} className="mr-1"/>Connected</span>
                  {user?.firstLoginProvider === 'TWITCH' ? (
                    <span className="text-xs text-muted-foreground italic ml-2" title="This is your primary sign-in method and cannot be unlinked.">(Primary sign-in method, cannot unlink)</span>
                  ) : (
                    <button
                      onClick={handleUnlinkTwitch}
                      className="px-3 py-1 border border-red-500 text-red-500 hover:bg-red-500/10 text-xs rounded-md"
                    >
                      Unlink
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleConnect('twitch')}
                  disabled={!!isConnectingOAuth}
                  className="px-4 py-2 bg-[#9146FF] hover:bg-[#772CE8] text-white text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9146FF]/50 flex items-center disabled:opacity-50"
                >
                  {isConnectingOAuth === 'twitch' ? (
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <TwitchIcon />
                  )}
                  {isConnectingOAuth === 'twitch' ? 'Connecting...' : 'Connect with Twitch'}
                </button>
              )}
            </div>
             {isTwitchLinked && ( // Twitch might not always provide email, but username is usually there
                 <p className="text-xs text-muted-foreground mt-1 pl-7">
                    Linked as {user.name || user.username}
                 </p>
            )}
          </div>
         {/* Kick Account Linking */}
         <div>
           <div className="flex items-center justify-between">
             <div className="flex items-center">
               <KickIcon />
               <span className="font-medium">Kick</span>
             </div>
             {isKickLinked ? (
               <div className="flex items-center">
                 <span className="text-sm text-success mr-3 flex items-center"><CheckCircle size={14} className="mr-1"/>Connected</span>
                 {user?.firstLoginProvider === 'KICK' ? (
                   <span className="text-xs text-muted-foreground italic ml-2" title="This is your primary sign-in method and cannot be unlinked.">(Primary sign-in, cannot unlink)</span>
                 ) : (
                   <button
                     onClick={handleUnlinkKick}
                     className="px-3 py-1 border border-red-500 text-red-500 hover:bg-red-500/10 text-xs rounded-md"
                   >
                     Unlink
                   </button>
                 )}
               </div>
             ) : (
               <button
                 onClick={() => handleConnect('kick')}
                 disabled={!!isConnectingOAuth}
                 className="px-4 py-2 bg-[#53FC18] hover:bg-[#49E215] text-black text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#53FC18]/50 flex items-center disabled:opacity-50"
               >
                 {isConnectingOAuth === 'kick' ? (
                   <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                 ) : (
                   <KickIcon />
                 )}
                 {isConnectingOAuth === 'kick' ? 'Connecting...' : 'Connect with Kick'}
               </button>
             )}
           </div>
            {isKickLinked && (
                <p className="text-xs text-muted-foreground mt-1 pl-7">
                   Linked as {user.name || user.username}
                </p>
           )}
         </div>
        </div>
      </div>

      <div className="bg-card p-6 rounded-lg shadow-md border border-border mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Active Devices</h2>
          {deviceSessions.length > 1 && (
            <button
              onClick={handleRevokeOtherSessions}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Sign out all other devices
            </button>
          )}
        </div>
        
        {isLoadingSessions ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : sessionsError ? (
          <p className="text-center text-error py-4">{sessionsError}</p>
        ) : deviceSessions.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No active sessions found</p>
        ) : (
          <div className="space-y-4">
            {deviceSessions.map((session) => (
              <div key={session.deviceId} className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start space-x-3">
                  <div className="text-muted-foreground mt-1">
                    {getDeviceIcon(session.deviceType)}
                  </div>
                  <div>
                    <p className="font-medium">
                      {session.deviceName || 'Unknown Device'}
                      {session.isCurrent && <span className="ml-2 text-xs text-primary">(This device)</span>}
                    </p>
                    {session.ipAddress && (
                      <p className="text-sm text-muted-foreground">IP: {session.ipAddress}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Last active: {formatLastActivity(session.lastActivity)}
                    </p>
                  </div>
                </div>
                {!session.isCurrent && (
                  <button
                    onClick={() => handleRevokeSession(session.deviceId)}
                    className="flex items-center px-3 py-1 border border-red-500 text-red-500 hover:bg-red-500/10 text-sm rounded-md"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Sign out
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card p-6 rounded-lg shadow-md border border-border">
        <h2 className="text-xl font-semibold mb-4">{mightNeedToSetPassword ? 'Set / Change Password' : 'Change Password'}</h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-muted-foreground">
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 bg-background border border-input rounded-md shadow-sm placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-muted-foreground">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 bg-background border border-input rounded-md shadow-sm placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
          {passwordMessage && (
            <p className={`text-sm ${passwordMessage.type === 'success' ? 'text-success' : 'text-error'}`}>
              {passwordMessage.text}
            </p>
          )}
          <button
            type="submit"
            disabled={isPasswordSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {isPasswordSubmitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AccountSettingsPage;